import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import errorMessage from '../../../helpers/errorMessage';
import { USER_COLUMNS } from './userColumns'
import EmptyState from '../../common/EmptyState';

// Same filter-menu pattern as FileTracking.js's table (filterDisplay='menu' +
// a per-column filters object), rather than a bespoke search box above the
// table - keeps every admin table in this app consistent. Needs the
// operator/constraints shape (not the simpler {value, matchMode} form) -
// that simpler form doesn't actually get a working Apply button in this
// PrimeReact version, which is what FileTracking's own working filters2
// state already uses instead of its unused, simpler `filters` object.
const emptyFilters = () => ({
    email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
});

// Every group this app checks anywhere - feature groups (frontend
// check_*_access in AccessHelper.js, now also enforced server-side via
// require_group) plus the environment groups (DevAccess/ProdAccess) added
// to keep the single shared Cognito pool split between dev and prod.
export const ALL_GROUPS = ['User', 'Admin', 'Analytics', 'ECS', 'Monitoring', 'ABS', 'InternalDevelopers', 'DevAccess', 'ProdAccess'];

export const Users = (props) => {
    const [users, setUsers] = useState(props.users)
    const [savingGroupsFor, setSavingGroupsFor] = useState(null)
    const [filters, setFilters] = useState(emptyFilters())

    useEffect(() => {
        setUsers(props.users)
    }, [props.users]);

    const onGroupsChange = (rowData, newGroups) => {
        const oldGroups = rowData.groups || [];
        const added = newGroups.filter(g => !oldGroups.includes(g));
        const removed = oldGroups.filter(g => !newGroups.includes(g));
        if (added.length === 0 && removed.length === 0) {
            return;
        }
        setSavingGroupsFor(rowData.user_id);
        Promise.all([
            ...added.map(group => axiosWithAuth.post(`/platforms/users/${rowData.user_id}/groups`, { group })),
            ...removed.map(group => axiosWithAuth.delete(`/platforms/users/${rowData.user_id}/groups/${group}`)),
        ]).then(() => {
            setUsers(prev => prev.map(u => u.user_id === rowData.user_id ? { ...u, groups: newGroups } : u));
        }).catch(err => {
            alert('Failed to update groups: ' + errorMessage(err));
        }).finally(() => {
            setSavingGroupsFor(null);
        });
    }

    const onResetPassword = (rowData) => {
        confirmDialog({
            message: `Force a password reset for ${rowData.email}? Their current password stops working immediately - they'll need to use "Forgot Password" on the login screen to set a new one.`,
            header: 'Confirm Password Reset',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                axiosWithAuth.post(`/platforms/users/${rowData.user_id}/reset-password`).then(() => {
                    alert(`Password reset for ${rowData.email}. They can set a new one via "Forgot Password" on the login screen.`);
                }).catch(err => {
                    alert('Failed to reset password: ' + errorMessage(err));
                });
            }
        });
    }

    const groupsBody = (rowData) => {
        return (
            <MultiSelect
                value={rowData.groups || []}
                options={ALL_GROUPS}
                onChange={(e) => onGroupsChange(rowData, e.value)}
                disabled={savingGroupsFor === rowData.user_id}
                placeholder="No groups"
                // display="chip" wrapped every selected group onto its own
                // line, growing the row taller the more groups a user had -
                // plain comma text truncates to one line instead, with the
                // full list still visible in the open dropdown panel.
                maxSelectedLabels={2}
                selectedItemsLabel="{0} groups"
                style={{ width: '14rem' }}
            />
        );
    }

    const actionsBody = (rowData) => {
        return <Button label="Reset Password" icon="pi pi-lock" className="p-button-sm p-button-outlined p-button-warning" onClick={() => onResetPassword(rowData)} />;
    }

    if (!users || users.length === 0) {
        return (
            <React.Fragment>
                <ConfirmDialog />
                <EmptyState icon="pi-users" title="No users yet for this Originator." message="Use Create User to invite the first one." />
            </React.Fragment>
        );
    }

    const platformUserColumns = USER_COLUMNS.map((col, i) => {
        if (col.field in users[0]) {
            // Groups now edit in place instead of just displaying chips -
            // same column, but require_group('Admin') already gates the
            // routes this calls, so only an admin ever reaches this page.
            const body = col.field === 'groups' ? groupsBody : col.body;
            const isEmail = col.field === 'email';
            return <Column key={col.field} field={col.field} header={col.header} style={col.style} body={body}
                sortable={col.field === 'groups' ? false : col.sortable} align={col.align}
                filter={isEmail} filterPlaceholder="Search by email" showFilterMenuOptions={false} />
        }
    })

    return (
        <React.Fragment>
        <ConfirmDialog />
        <DataTable value={users} dataKey="email" emptyMessage="No users match this filter."
            paginator paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" rows={5} rowsPerPageOptions={[5, 10, 20, 50]} responsiveLayout="scroll" stripedRows sortField="email"
            filterDisplay="menu" filters={filters} onFilter={(e) => setFilters(e.filters)}>
            {platformUserColumns}
            <Column header="Actions" body={actionsBody} />
        </DataTable>
        </React.Fragment>
    );
}
